// Revival Planner — what the scanner has produced, measured on every scanned
// quote. It used to count a page: "Quotes Extracted" was the length of
// GET /revival/scanner/recent/?limit=12 (always 12 once 12 existed), and
// confidence / matched / the chart were computed from that page or the newest
// 100. The count now comes from the scanner summary (every scanned quote) and
// the rest from the full list.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsPhone from '../../hooks/useIsPhone';
import { DataTable } from '../ui/table';
import Badge from '../ui/badge';
import Button from '../ui/button';
import RevivalHeader, { StatStrip } from './RevivalHeader';
import QuoteWindow from './QuoteWindow';
import PlanChart, { monthlyScanned } from './PlanChart';
import { count, money, moneyWhole, quoteTotal, shortDate, statusLabel, statusTone, useRevivalQuotes, useScanSummary } from './revivalData';
import './revival.css';

/** ai_confidence: a value up to 1 reads as a fraction, above 1 as a percent. */
const pct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n <= 1 ? n * 100 : n;
};

const Planner = () => {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const { rows, loading, error, reload } = useRevivalQuotes();
  const { summary } = useScanSummary();
  const [open, setOpen] = useState(null);

  const scanned = useMemo(
    () => rows.filter((q) => q.source === 'scanner').sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))),
    [rows]
  );
  const confidences = scanned.map((q) => pct(q.ai_confidence)).filter((n) => n !== null);
  const avgConfidence = confidences.length ? `${Math.round(confidences.reduce((a, b) => a + b, 0) / confidences.length)}%` : '—';
  const matched = scanned.filter((q) => q.customer_id || q.customer_pk).length;
  const months = useMemo(() => monthlyScanned(scanned), [scanned]);
  const recent = scanned.slice(0, 12);

  const columns = [
    { key: 'created_at', label: 'Scanned', render: (q) => <span className="rv-cell-dim">{shortDate(String(q.created_at || '').slice(0, 10))}</span> },
    { key: 'customer_name', label: 'Customer', render: (q) => <span className="rv-cell-name">{q.customer_name || '—'}</span> },
    { key: 'address', label: 'Address', render: (q) => <span className="rv-cell-clip rv-cell-dim">{q.address || '—'}</span> },
    { key: 'status', label: 'Status', render: (q) => <Badge tone={statusTone(q.status || 'draft')}>{statusLabel(q.status || 'draft')}</Badge> },
    { key: 'total', label: 'Total', num: true, sortValue: (q) => quoteTotal(q), render: (q) => money(quoteTotal(q)) },
  ];

  return (
    <div className="rv-screen">
      <RevivalHeader
        title="Planner"
        totals={summary ? [`${count(summary.total_scanned)} scanned`, moneyWhole(summary.total_scanned_value)] : []}
        primary={<Button variant="primary" size="sm" onClick={() => navigate('/revival/scanner')}>Scan a ticket</Button>}
      />
      <StatStrip
        stats={[
          { label: 'Quotes scanned', value: count(summary?.total_scanned) },
          { label: 'Avg confidence', value: loading ? '—' : avgConfidence, note: confidences.length ? `${count(confidences.length)} with a score` : null },
          { label: 'Matched to a customer', value: loading ? '—' : count(matched) },
          { label: 'Scanned value', value: moneyWhole(summary?.total_scanned_value) },
        ]}
      />
      {error ? <div className="rv-error" role="alert">{error}</div> : null}

      <h2 className="rv-section-title">Scanned value by month</h2>
      {loading ? <div className="rv-loading">Loading…</div> : <PlanChart data={months} />}

      <h2 className="rv-section-title">Recent scans</h2>
      {loading ? <div className="rv-loading">Loading…</div> : isPhone ? (
        <div className="rv-list">
          {recent.map((q) => (
            <button key={q.id} type="button" className="rv-row" onClick={() => setOpen(q)}>
              <span className="rv-row-main"><b>{q.customer_name || '—'}</b><span>{q.address || '—'}</span></span>
              <span className="rv-row-end"><b>{money(quoteTotal(q))}</b><span>{statusLabel(q.status || 'draft')}</span></span>
            </button>
          ))}
          {!recent.length ? <div className="rv-loading">No scans yet. Scan a ticket to get started.</div> : null}
        </div>
      ) : (
        <DataTable columns={columns} rows={recent} onRowClick={setOpen} empty="No scans yet. Scan a ticket to get started." />
      )}
      {open ? <QuoteWindow quote={open} onClose={() => setOpen(null)} onDeleted={reload} /> : null}
    </div>
  );
};

export default Planner;
